import { z } from 'zod';

// The ONE literal for a promo code's shape (THI-200). Every other schema that
// needs a promo code — including `upgradeSubscriptionSchema.promoCode` in
// `subscription.ts` — imports `promotionCodeSchema` rather than re-stating
// this regex.
export const promotionCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z0-9-]{3,20}$/, 'Mã khuyến mãi phải gồm 3-20 ký tự chữ hoa, số hoặc dấu gạch ngang');

export const createPromotionSchema = z
  .object({
    code: promotionCodeSchema.optional().nullable(),
    name: z.string().trim().min(1).max(120),
    // THI-200 follow-up: the one-sentence marketing headline for the landing
    // announcement bar and the in-app offer banner. `''` is normalised to
    // `null` client-side (PromotionSheet.tsx), same convention as `ownerLabel`.
    headline: z.string().trim().max(120).optional().nullable(),
    kind: z.literal('PERCENT_OFF').default('PERCENT_OFF'),
    percentOff: z.number().int().min(1).max(90),
    startsAt: z.string().datetime(),
    endsAt: z.string().datetime(),
    planCodes: z.array(z.enum(['PLUS', 'PRO', 'CENTER'])).min(1),
    appliesToMonthly: z.boolean().default(true),
    appliesToAnnual: z.boolean().default(true),
    active: z.boolean().default(true),
    ownerLabel: z.string().trim().max(80).optional().nullable(),
    maxRedemptions: z.number().int().min(1).optional().nullable(),
    note: z.string().max(500).optional().nullable(),
  })
  .refine((v) => new Date(v.startsAt).getTime() < new Date(v.endsAt).getTime(), {
    message: 'startsAt phải trước endsAt',
    path: ['endsAt'],
  })
  .refine((v) => v.appliesToMonthly || v.appliesToAnnual, {
    message: 'Khuyến mãi phải áp dụng cho ít nhất một chu kỳ thanh toán',
    path: ['appliesToMonthly'],
  });

export type CreatePromotionInput = z.infer<typeof createPromotionSchema>;

// Written out longhand, NOT derived from createPromotionSchema.partial()
// (§4.1 in docs/INVARIANTS.md): .partial() does not strip .default(), so a
// PATCH of just { percentOff } would parse into { percentOff, kind:
// 'PERCENT_OFF', appliesToMonthly: true, appliesToAnnual: true, active: true }
// and silently rewrite every other field on a live promotion. Every field
// here is optional with no default at all. `code` is accepted here — the
// route enforces immutability once a promotion has been redeemed (Task 8).
export const updatePromotionSchema = z
  .object({
    code: promotionCodeSchema.optional().nullable(),
    name: z.string().trim().min(1).max(120).optional(),
    headline: z.string().trim().max(120).optional().nullable(),
    kind: z.literal('PERCENT_OFF').optional(),
    percentOff: z.number().int().min(1).max(90).optional(),
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().optional(),
    planCodes: z.array(z.enum(['PLUS', 'PRO', 'CENTER'])).min(1).optional(),
    appliesToMonthly: z.boolean().optional(),
    appliesToAnnual: z.boolean().optional(),
    active: z.boolean().optional(),
    ownerLabel: z.string().trim().max(80).optional().nullable(),
    maxRedemptions: z.number().int().min(1).optional().nullable(),
    note: z.string().max(500).optional().nullable(),
  })
  .refine(
    (v) => v.startsAt === undefined || v.endsAt === undefined || new Date(v.startsAt).getTime() < new Date(v.endsAt).getTime(),
    { message: 'startsAt phải trước endsAt', path: ['endsAt'] },
  )
  .refine((v) => v.appliesToMonthly !== false || v.appliesToAnnual !== false, {
    message: 'Khuyến mãi phải áp dụng cho ít nhất một chu kỳ thanh toán',
    path: ['appliesToMonthly'],
  });

export type UpdatePromotionInput = z.infer<typeof updatePromotionSchema>;

export type PromotionCodeInput = z.infer<typeof promotionCodeSchema>;
